import * as React from "react";

import {Radio, RadioGroup} from "@headlessui/react";
import {Decimal} from "decimal.js";
import {motion} from "framer-motion";
import {produce} from "immer";
import {Drawer} from "vaul";

import * as auth from "@client/features/auth";
import * as subscriptions from "@client/features/subscriptions";

import * as constants from "@client/constants";
import * as dates from "@client/dates";
import * as router from "@client/router";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";

import {TermsOfService} from "@client/components/TermsOfService";
import * as widgets from "@client/forms/widgets";
import {ActionsDefinition, OptionsDefinition} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import * as onboarding from "@client/journeys/onboarding";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {useAppDispatch, useAppSelector} from "@client/store";
import {classNames, models, server} from "@reactivated";

export const BACKGROUND_IMAGES = {
    main: "/static/images/main-bg.png",
    workouts: "/static/images/landing-workouts-bg.png",
    nutrition: "/static/images/landing-nutrition-bg.png",
    habit: "/static/images/landing-workouts-bg.jpg",
} as const;

export const BUTTON_CLASS =
    "text-white rounded-md text-xl/6 font-inter font-light px-6 py-4 bg-[#7D50FF] hover:bg-[#7D50FF]/95 disabled:opacity-50";

export const BUTTON_CLASS_SMALL =
    "text-white rounded-md text-sm/6 font-inter font-medium px-4 py-1.5 bg-[#7D50FF] hover:bg-[#7D50FF]/95 disabled:opacity-50";

export const BUTTON_CLASS_XS =
    "rounded-md text-xs font-inter font-normal px-3 py-1.5 bg-indigo-100 text-indigo-800 active:text-white active:bg-indigo-700 disabled:opacity-50";

export const INPUT_WRAPPER =
    "has-focus:ring-[#7D50FF] has-focus:ring-2 flex items-center pr-1 w-full rounded-sm bg-[#332F4A]";

export const INPUT_CLASS = `scheme-dark 
    min-w-0
    flex-1 border-0 bg-transparent px-6 py-4 
    font-inter font-extralight
    placeholder:opacity-70 placeholder:font-extralight placeholder:text-white placeholder:font-inter 
    focus:outline-hidden focus:ring-0`;

const TARGET_TEMPLATE: Omit<server.journal.day.ConsumerTarget.input, "uuid"> = {
    name: "Macros",
    calories_target: null,
    protein_target: null,
    protein_mode: "AT_LEAST",
    fat_target: null,
    carbs_target: null,
    fiber_target: null,
    net_carbs_target: null,
};

function Header(props: {back: () => void; more?: React.ReactNode}) {
    return (
        <div className="relative flex items-center gap-20">
            <button
                type="button"
                onClick={props.back}
                className="
                    flex size-10 items-center justify-center rounded-sm bg-[#332F4A]
                "
            >
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 17 18"
                    fill="none"
                    className="size-4 rotate-180 text-[#9874FF]"
                >
                    <path
                        d="M7.5 1.25L15 9.25M15 9.25L7.5 16.75M15 9.25H0"
                        stroke="currentColor"
                        strokeWidth="2"
                    />
                </svg>
            </button>
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 73 26"
                fill="none"
                className="h-[26px] w-[73px]"
            >
                <g id="Group 83">
                    <path
                        id="J"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M16.9549 0.265625H13.5602V16.1073H13.5786C13.5786 18.9296 11.2906 21.2176 8.46827 21.2176C5.64592 21.2176 3.35796 18.9296 3.35796 16.1073H0C0 20.7842 3.79137 24.5756 8.46827 24.5756C13.1452 24.5756 16.9365 20.7842 16.9365 16.1073H16.9549V0.265625Z"
                        fill="white"
                    />
                    <g id="O">
                        <path
                            id="Subtract"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M40.5045 5.11579L42.5968 3.02356L42.5705 3.00203C42.3434 2.81564 42.1095 2.63708 41.8693 2.46677C39.8294 1.02031 37.3359 0.169922 34.6465 0.169922C27.7429 0.169922 22.1465 5.76636 22.1465 12.6699C22.1465 19.5735 27.7429 25.1699 34.6465 25.1699C41.55 25.1699 47.1465 19.5735 47.1465 12.6699C47.1465 11.2074 46.8949 9.80166 46.432 8.49483C46.3185 8.17443 46.1923 7.86004 46.054 7.5523L46.0246 7.48685L43.7541 9.7574C44.0467 10.6734 44.2053 11.6513 44.2053 12.6699C44.2053 17.9491 39.9257 22.2287 34.6465 22.2287C29.3673 22.2287 25.0877 17.9491 25.0877 12.6699C25.0877 7.39073 29.3673 3.1111 34.6465 3.1111C36.7066 3.1111 38.6091 3.76052 40.168 4.86599C40.2821 4.94684 40.3942 5.03013 40.5045 5.11579Z"
                            fill="white"
                        />
                        <path
                            id="Vector 1 (Stroke)"
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M45.9055 6.04593L35.153 16.7985L31.1484 12.7939L33.5342 10.4082L35.153 12.0269L43.5198 3.66016L45.9055 6.04593Z"
                            fill="#EF8165"
                        />
                    </g>
                    <path
                        id="Y"
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M49.2043 0.266418L59.0605 17.3378V25.1598H62.4551V17.5304L72.4225 0.266418H68.5027L60.8134 13.5846L53.1241 0.266418H49.2043Z"
                        fill="white"
                    />
                </g>
            </svg>
            {props.more != null && <div className="absolute right-0">{props.more}</div>}
        </div>
    );
}

export function Page(props: {
    next: (() => void) | {label: string; onClick: () => unknown};
    back: () => void;
    children: React.ReactNode;
    disabled?: boolean;
    secondary?: React.ReactNode;
    more?: React.ReactNode;
}) {
    return (
        <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            style={{
                backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
            }}
            className="dark min-h-svh w-full scroll-smooth bg-cover bg-top font-inter"
        >
            <div className="py-safe mx-auto flex h-dvh max-w-xl flex-col px-8 text-white">
                <Header back={props.back} more={props.more} />
                <form className="mt-8 flex flex-1 flex-col justify-between">
                    {props.children}
                    <div className="flex flex-col gap-2">
                        <typography.AsyncButton
                            type="button"
                            className={BUTTON_CLASS}
                            onClick={
                                typeof props.next == "function"
                                    ? props.next
                                    : props.next.onClick
                            }
                            disabled={props.disabled}
                        >
                            {typeof props.next == "function"
                                ? "Next"
                                : props.next.label}
                        </typography.AsyncButton>
                        {props.secondary}
                    </div>
                </form>
            </div>
        </motion.div>
    );
}

export function VaulDrawer({
    trigger,
    children,
    onOpen,
}: React.PropsWithChildren<{trigger: React.JSX.Element; onOpen?: () => void}>) {
    const [open, setOpen] = React.useState(false);

    return (
        <Drawer.Root
            open={open}
            onOpenChange={(open) => {
                setOpen(open);
                if (open) {
                    onOpen?.();
                }
            }}
            autoFocus
        >
            <Drawer.Trigger className="contents">{trigger}</Drawer.Trigger>
            <Drawer.Portal>
                <Drawer.Overlay className="fixed inset-0" />
                <Drawer.Description></Drawer.Description>
                <Drawer.Content
                    className="
                        fixed inset-x-0 bottom-0 h-fit bg-[#332F4A]
                        shadow-[0_-16px_11.6px_0_#0000001A] outline-hidden
                    "
                >
                    <div className="flex flex-col gap-4">
                        <button
                            className="
                                mt-2 mr-2 ml-auto size-10 rounded-sm bg-[#22173066]/40
                            "
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 40 40"
                                fill="none"
                                className="size-10 text-[#9874FF]"
                            >
                                <path
                                    d="M14.9843 14.9843L24.5645 24.5645"
                                    stroke="currentColor"
                                    strokeWidth="1.80645"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M14.9843 24.5645L24.5645 14.9844"
                                    stroke="currentColor"
                                    strokeWidth="1.80645"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                        {children}
                    </div>
                </Drawer.Content>
            </Drawer.Portal>
        </Drawer.Root>
    );
}

function TermsParagraph() {
    return (
        <p>
            By creating your account, you agree to our{" "}
            <VaulDrawer
                trigger={
                    <span className="font-normal text-[#7D50FF]">terms of service</span>
                }
            >
                <div
                    className="
                        py-safe mx-auto max-h-[40dvh] w-full max-w-xl overflow-y-auto
                        px-8 text-lg font-extralight text-white/85
                    "
                >
                    <TermsOfService />
                </div>
            </VaulDrawer>
            .
        </p>
    );
}

export function SimpleRadioSelect<
    TEnum extends string,
    TField extends Extract<forms.PFieldHandler<TEnum>, {type: "select"}>,
>(props: {
    field: TField;
    descriptions?: Record<TEnum, React.ReactNode | false>;
    labels?: Partial<Record<TEnum, string>>;
    selected: TEnum | null;
}) {
    const filtered = props.field.options
        .map(([setting, label]) => {
            if (setting == null) return null;
            const description: string | React.ReactNode | false =
                props.descriptions?.[setting];

            if (description == false) {
                return null;
            }
            return {
                setting,
                label,
                description,
                disabled: false,
            };
        })
        .filter((o) => o != null);

    const classes = `
        flex flex-1 items-center justify-center rounded-sm bg-[#332F4A] p-3 text-[16px]
        font-light text-white ring-1 ring-[#453F63]
        hover:bg-[#453F63]
        data-checked:bg-[#7D50FF] data-checked:text-white data-checked:ring-0
        data-checked:hover:bg-[#7D50FF]
        data-focus:ring-2 data-focus:ring-[#7D50FF] data-focus:ring-offset-2
        data-focus:data-checked:ring-2
        sm:flex-1
        [&:not([data-focus])]:[&:not([data-checked])]:ring-inset
    `;

    return (
        <RadioGroup
            value={props.selected}
            onChange={props.field.onChange}
            className="flex gap-4"
        >
            {filtered.map(({setting, disabled, label: defaultLabel}) => (
                <Radio
                    key={setting}
                    value={setting}
                    disabled={disabled}
                    className={classNames(
                        disabled
                            ? "cursor-not-allowed opacity-25"
                            : "cursor-pointer focus:outline-hidden",
                        classes,
                    )}
                >
                    {props.labels?.[setting] ?? defaultLabel}
                </Radio>
            ))}
        </RadioGroup>
    );
}

function useRegistration(options: {
    mode: "registration" | "onboarding" | "welcome";
    email: string | null;
    profile: server.core.rpc.Profile.output | null;
    activeProgram: server.journal.workouts.ActiveProgram.output | null;
    lastWeight?: string | null;
}): {view: React.ReactNode; options: OptionsDefinition; actions: ActionsDefinition} {
    const email = options.email ?? "";

    const iap = React.useMemo(
        () => (localStorage.getItem("iap") == "iap" ? "iap" : "trial"),
        [],
    );
    console.log("JOYIAP", iap);

    const PRODUCT_NAME = useAppSelector((state) =>
        subscriptions.getSubscriptionProductName(state, "basic"),
    );

    React.useEffect(() => {
        void hardware.nativeOnly(() => {
            void hardware.Keyboard.setResizeMode({mode: hardware.KeyboardResize.Body});
        });
        return () => {
            void hardware.nativeOnly(() => {
                void hardware.Keyboard.setResizeMode({
                    mode: hardware.KeyboardResize.Native,
                });
            });
        };
    }, []);

    const {platform} = React.use(hardware.getAppInfo());

    const subscriptionDollarsPerMonth = platform === "ios" ? "4.99" : "5";

    function generateWeightOptions(unitSystem: "IMPERIAL" | "METRIC"): number[] {
        if (unitSystem === "IMPERIAL") {
            return Array.from({length: 93}, (_, i) => i * 5 + 40);
        } else {
            // METRIC
            return Array.from({length: 47}, (_, i) => i * 5 + 20);
        }
    }

    function calculateDefaultWeights(lastWeight: string | null | undefined): {
        defaultWeightLb: string | null;
        defaultWeightKg: string | null;
    } {
        if (lastWeight == null) {
            return {defaultWeightLb: null, defaultWeightKg: null};
        }

        const lastWeightInKgDecimal = new Decimal(lastWeight);
        let calculatedWeightLb: string | null = null;
        let calculatedWeightKg: string | null = null;

        // Calculate for Imperial
        const lastWeightInLbs = lastWeightInKgDecimal.mul(2.20462);
        const lbOptionsValues = generateWeightOptions("IMPERIAL");
        if (lbOptionsValues.length > 0) {
            calculatedWeightLb = lbOptionsValues
                .reduce((prev, curr) =>
                    Math.abs(curr - lastWeightInLbs.toNumber()) <
                    Math.abs(prev - lastWeightInLbs.toNumber())
                        ? curr
                        : prev,
                )
                .toString();
        }

        // Calculate for Metric
        const kgOptionsValues = generateWeightOptions("METRIC");
        if (kgOptionsValues.length > 0) {
            calculatedWeightKg = kgOptionsValues
                .reduce((prev, curr) =>
                    Math.abs(curr - lastWeightInKgDecimal.toNumber()) <
                    Math.abs(prev - lastWeightInKgDecimal.toNumber())
                        ? curr
                        : prev,
                )
                .toString();
        }

        return {
            defaultWeightLb: calculatedWeightLb,
            defaultWeightKg: calculatedWeightKg,
        };
    }

    const dispatch = useAppDispatch();
    const handler = onboarding.useHooks({
        diet: "LEANGAINS",
        requireName: false,
        profile: options.profile,
    });

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const {defaultFeet, defaultInches} =
        options.profile == null || options.profile.height == null
            ? {defaultFeet: null, defaultInches: null}
            : {
                  defaultFeet: new Decimal(options.profile.height)
                      .div(2.54)
                      .div(12)
                      .floor()
                      .toString() as "5",
                  defaultInches: new Decimal(options.profile.height)
                      .div(2.54)
                      .mod(12)
                      .floor()
                      .toString() as "11",
              };

    const initialUnits = options.profile?.is_imperial === false ? "METRIC" : "IMPERIAL";

    const allDefaultWeights = calculateDefaultWeights(options.lastWeight);

    const [settings, setSettings] = React.useState<{
        mfp: "NO" | "YES" | null;
        gender: "MALE" | "FEMALE" | null;
        units: "IMPERIAL" | "METRIC";
        workout: "NONE" | "DUMBBELL" | "BARBELL" | "SKIP" | null;
        feet: "2" | "3" | "4" | "5" | "6" | "7" | "8" | null;
        weightLb: string | null;
        weightKg: string | null;
        inches:
            | "0"
            | "1"
            | "2"
            | "3"
            | "4"
            | "5"
            | "6"
            | "7"
            | "8"
            | "9"
            | "10"
            | "11"
            | null;
    }>({
        mfp:
            options.profile != null
                ? options.profile.user.casted_dismissed_announcements.includes(
                      "HAS_USED_MYFITNESSPAL",
                  )
                    ? "YES"
                    : "NO"
                : null,
        gender:
            options.profile != null
                ? options.profile.gender == "m"
                    ? "MALE"
                    : "FEMALE"
                : null,
        units: initialUnits,
        workout: null,
        feet: defaultFeet,
        inches: defaultInches,
        weightLb: allDefaultWeights.defaultWeightLb,
        weightKg: allDefaultWeights.defaultWeightKg,
    });

    const context = useSiteContext();

    const url = new URL(context.request.url);
    const defaultRoute = options.mode == "registration" ? "about" : "register";
    const [route, _setRoute] = React.useState<
        | "about"
        | "goal"
        | "activity"
        | "register"
        | "workout"
        | "escape"
        | "fine"
        | "macros"
    >((url.searchParams.get("route") as "about" | null) ?? defaultRoute);

    const [targets, setTargets] = React.useState<
        (Pick<
            server.journal.nutrition.Target.input,
            | "calories_target"
            | "protein_target"
            | "fat_target"
            | "carbs_target"
            | "fiber_target"
            | "net_carbs_target"
        > | null)[]
    >([]);
    const targetIndex = 0;
    const [hasCustomMacros, setHasCustomMacros] = React.useState(false);

    const setRoute = (
        nextRoute:
            | "about"
            | "goal"
            | "activity"
            | "macros"
            | "register"
            | "workout"
            | "escape"
            | "fine",
    ) => {
        _setRoute(nextRoute);
        void analytics.track("CHANGED_REGISTRATION_ROUTE", {
            route_name: nextRoute,
        });
    };
    const {skipLabel, skipText} =
        options.activeProgram == null
            ? {
                  skipLabel: "None",
                  skipText: "I don't want a program. I'll do my own thing.",
              }
            : {skipLabel: "Keep current", skipText: "Keep my existing program"};

    const settingsForm = forms.usePform(
        {
            mfp: {
                type: "select",
                widget: "quick",
                options: [
                    ["YES", "Yes"],
                    ["NO", "No"],
                ],
            },
            units: {
                type: "select",
                required: true,
                widget: "select",
                options: [
                    ["IMPERIAL", "feet and inches"],
                    ["METRIC", "centimeters"],
                ],
            },
            feet: {
                type: "select",
                required: false,
                widget: "select",
                options: [
                    [null, "Feet"],
                    ["2", "2 ft"],
                    ["3", "3 ft"],
                    ["4", "4 ft"],
                    ["5", "5 ft"],
                    ["6", "6 ft"],
                    ["7", "7 ft"],
                    ["8", "8 ft"],
                ],
            },
            weightLb: {
                type: "select",
                required: false,
                widget: "select",
                options: [[null, "Select weight"] as [string | null, string]].concat(
                    generateWeightOptions("IMPERIAL").map((value) => [
                        value.toString(),
                        `${value} lbs`,
                    ]),
                ),
            },
            weightKg: {
                type: "select",
                required: false,
                widget: "select",
                options: [[null, "Select weight"] as [string | null, string]].concat(
                    generateWeightOptions("METRIC").map((value) => [
                        value.toString(),
                        `${value} kg`,
                    ]),
                ),
            },
            inches: {
                type: "select",
                required: false,
                widget: "select",
                options: [
                    [null, "Inches"],
                    ["0", "0 in"],
                    ["1", "1 in"],
                    ["2", "2 in"],
                    ["3", "3 in"],
                    ["4", "4 in"],
                    ["5", "5 in"],
                    ["6", "6 in"],
                    ["7", "7 in"],
                    ["8", "8 in"],
                    ["9", "9 in"],
                    ["10", "10 in"],
                    ["11", "11 in"],
                ],
            },
            gender: {
                type: "select",
                widget: "quick",
                options: [
                    ["MALE", "Male"],
                    ["FEMALE", "Female"],
                ],
            },
            workout: {
                type: "select",
                widget: "select",
                options: [
                    ["NONE", "Absolute beginner"],
                    ["DUMBBELL", "Dumbbells"],
                    ["BARBELL", "Barbells"],
                    ["SKIP", skipLabel],
                ],
            },
        },
        settings,
        setSettings,
    );

    React.useEffect(() => {
        if (settingsForm.values.feet != null && settingsForm.values.inches != null) {
            const heightInCm = new Decimal(settingsForm.values.inches)
                .add(new Decimal(settingsForm.values.feet).mul(12))
                .mul(2.54)
                .toString();
            handler.profileForm.setValues((prev) => ({...prev, height: heightInCm}));
        } else {
            handler.profileForm.setValues((prev) => ({...prev, height: null}));
        }
    }, [settingsForm.values.feet, settingsForm.values.inches]);

    React.useEffect(() => {
        if (settingsForm.values.weightLb != null) {
            const weightInKg = new Decimal(settingsForm.values.weightLb)
                .mul("0.45359237")
                .toString();
            handler.profileForm.setValues((prev) => ({...prev, weight: weightInKg}));
        } else {
            handler.profileForm.setValues((prev) => ({...prev, weight: null}));
        }
    }, [settingsForm.values.weightLb]);

    React.useEffect(() => {
        if (settingsForm.values.weightKg != null) {
            handler.profileForm.setValues((prev) => ({
                ...prev,
                weight: settingsForm.values.weightKg,
            }));
        } else {
            handler.profileForm.setValues((prev) => ({...prev, weight: null}));
        }
    }, [settingsForm.values.weightKg]);

    React.useEffect(() => {
        handler.profileForm.setValues((prev) => ({
            ...prev,
            gender: settingsForm.values.gender == "MALE" ? "m" : "f",
        }));
    }, [settingsForm.values.gender]);

    const isProfileComplete =
        settingsForm.values.mfp != null &&
        settingsForm.values.gender != null &&
        handler.profileForm.values.date_of_birth != null &&
        handler.profileForm.values.height != null &&
        handler.profileForm.values.weight != null;

    const liveTarget = hasCustomMacros
        ? {...TARGET_TEMPLATE, ...targets[targetIndex], uuid: crypto.randomUUID()}
        : {
              ...TARGET_TEMPLATE,
              calories_target: handler.general.results.targets[0].calories_nearest,
              protein_target: handler.general.results.targets[0].protein_nearest,
              fat_target: null,
              carbs_target: null,
              uuid: crypto.randomUUID(),
          };

    const handleChallengeRegistration = async () => {
        const tag = await hardware.getTag();

        const {date_of_birth, height} = handler.profileForm.values;

        const {gender, mfp, workout} = settingsForm.values;

        if (
            gender == null ||
            date_of_birth == null ||
            height == null ||
            workout == null ||
            mfp == null
        ) {
            void analytics.track("ERROR_REGISTERING", {registration_method: "method"});
            alert("Please fill out all fields");
            return;
        }

        const pendingRegistration: models["journal.day.Method"] = {
            registration: {
                email,
                with_notification: false,
                invite_code: "method",
                recipe_uuid: null,
                source: tag ?? "method",
                platform,
            },
            gender,
            mfp,
            date_of_birth,
            height,
            workout_experience: workout,
            units: settingsForm.values.units,
            targets: [liveTarget],
            activity: handler.settingsForm.values.activity,
            goal: handler.settingsForm.values.goal,
            weightLb: settingsForm.values.weightLb,
            weightKg: settingsForm.values.weightKg,
            iap_method: iap,
        };

        analytics.register({pendingRegistration});

        void analytics.track("REGISTERING", {registration_method: "method"});

        if (iap == "iap" && platform == "ios") {
            setIsSubmitting(true);
            void analytics.track("PROMPTED_IAP_REGISTRATION", {
                registration_email: email,
            });
            dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration});

            const purchase = await CdvPurchase.store
                .get(PRODUCT_NAME)
                ?.getOffer()
                ?.order();

            if (purchase?.code == CdvPurchase.ErrorCode.PAYMENT_CANCELLED) {
                dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration: null});
                void analytics.track("IAP_REGISTRATION_CANCELED", {
                    registration_email: email,
                });
                setIsSubmitting(false);
            } else if (purchase?.isError) {
                dispatch({type: "SET_PENDING_REGISTRATION", pendingRegistration: null});
                void analytics.track("IAP_REGISTRATION_ERROR", {
                    registration_email: email,
                });
                setIsSubmitting(false);
                alert("Error purchasing subscription. Contact support@joyapp.com");
            }

            return;
        }

        setIsSubmitting(true);
        const result = await server.journal.day.method(pendingRegistration);

        if (result.type == "invalid") {
            setIsSubmitting(false);
            alert("Error registering");
        } else if (result.type == "success") {
            const profile = result.data;
            void analytics.registered(profile.user.tracking_key, "method", iap);
            await dispatch(auth.postLogin(profile, true));
            router.push("today.journal", {});
        }
    };

    const handleFinishSetup = async () => {
        const {date_of_birth, height} = handler.profileForm.values;

        const {gender, mfp, workout} = settingsForm.values;

        if (
            gender == null ||
            date_of_birth == null ||
            height == null ||
            workout == null ||
            mfp == null
        ) {
            alert("Please fill out all fields");
            return;
        }

        const pendingRegistration: models["journal.day.Method"] = {
            registration: null,
            gender,
            mfp,
            date_of_birth,
            height,
            workout_experience: workout,
            units: settingsForm.values.units,
            targets: [liveTarget],
            activity: handler.settingsForm.values.activity,
            goal: handler.settingsForm.values.goal,
            weightLb: settingsForm.values.weightLb,
            weightKg: settingsForm.values.weightKg,
            iap_method: iap,
        };

        const result = await server.journal.day.onboarding({
            ...pendingRegistration,
            today: dates.formatISO(new Date(), {representation: "date"}),
        });

        if (result.type == "invalid") {
            alert("Error registering");
        } else if (result.type == "success") {
            if (options.mode == "welcome") {
                void analytics.track("FINISHED_SETUP_FROM_WELCOME");
                dispatch({type: "HIDE_SETUP"});
            }

            router.push("today.journal", {});
        }
    };

    const handleLegacyRegistration = async () => {
        const tag = await hardware.getTag();

        void analytics.track("REGISTERING", {registration_method: "app"});

        const result = await server.core.rpc.register({
            email,
            with_notification: false,
            invite_code: "app",
            recipe_uuid: null,
            source: tag ?? "app",
            platform,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });

        if (result.type == "invalid") {
            alert("Error registering");
        } else if (result.type == "success") {
            const profile = result.data;
            void analytics.registered(profile.user.tracking_key, "app", "trial");
            await dispatch(auth.postLogin(profile, true));

            router.push("base.index", {});
        }
    };

    if (route == "escape") {
        return {
            options: {
                title: "The fine print",
                scene: {kind: "BLANK"},
            },
            view: (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    style={{
                        backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
                    }}
                    className="
                        dark min-h-svh w-full scroll-smooth bg-cover bg-top font-inter
                    "
                >
                    <div
                        className="
                            py-safe mx-auto flex h-dvh max-w-xl flex-col px-8 text-white
                        "
                    >
                        <Header back={() => setRoute("about")} />
                        <form
                            className="mt-8 flex flex-1 flex-col justify-between"
                            onSubmit={(event) => {
                                event.preventDefault();
                                setRoute("fine");
                            }}
                        >
                            <h1 className="text-3xl font-extralight">The fine print</h1>
                            <div
                                className="
                                    flex flex-col gap-12 py-8 text-xl font-extralight
                                "
                            >
                                <p>
                                    Joy will help you track your nutrition, workouts and
                                    habits.
                                </p>
                                <p>It‘s free for 7 days.</p>
                                <p>
                                    Afterwards, it’s just ${subscriptionDollarsPerMonth}{" "}
                                    a month.
                                </p>
                                <TermsParagraph />
                            </div>
                            <div className="flex flex-col gap-2">
                                <typography.AsyncButton
                                    type="button"
                                    className={BUTTON_CLASS}
                                    forceLoading={isSubmitting}
                                    onClick={handleLegacyRegistration}
                                >
                                    Register
                                </typography.AsyncButton>
                            </div>
                        </form>
                    </div>
                </motion.div>
            ),
            actions: [],
        };
    }

    if (route == "fine") {
        return {
            options: {
                title: "The fine print",
                scene: {kind: "BLANK"},
            },
            view: (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    style={{
                        backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
                    }}
                    className="
                        dark min-h-svh w-full scroll-smooth bg-cover bg-top font-inter
                    "
                >
                    <div
                        className="
                            py-safe mx-auto flex h-dvh max-w-xl flex-col px-8 text-white
                        "
                    >
                        <Header back={() => setRoute("workout")} />
                        <form
                            className="mt-8 flex flex-1 flex-col justify-between"
                            onSubmit={(event) => {
                                event.preventDefault();
                                setRoute("fine");
                            }}
                        >
                            <h1 className="text-3xl font-extralight">The fine print</h1>
                            <div
                                className="
                                    flex flex-1 flex-col gap-8 py-8 text-xl
                                    font-extralight
                                "
                            >
                                <p>We’re almost done.</p>
                                <p>Joy is free for 7 days.</p>
                                <p>
                                    Afterwards, it’s just ${subscriptionDollarsPerMonth}{" "}
                                    a month.
                                </p>
                                {iap == "trial" && (
                                    <p>
                                        You will <em>not be billed</em> unless you
                                        subscribe at the end of the trial.
                                    </p>
                                )}
                                {/*
                                <p>
                                    If you{" "}
                                    <VaulDrawer
                                        trigger={
                                            <span className="font-normal text-[#7D50FF]">
                                                complete the challenge
                                            </span>
                                        }
                                    >
                                        <div className="w-full max-w-xl mx-auto text-white/85 font-extralight text-lg py-safe px-8">
                                            <h1 className="text-2xl font-extralight">
                                                Completing the challenge
                                            </h1>
                                            <ul className="list-decimal list-inside">
                                                <li>
                                                    Weigh yourself 21 days in a row.
                                                </li>
                                                <li>Log food 21 days in a row.</li>
                                                <li>
                                                    Complete 9 workouts across 21 days
                                                </li>
                                                <li>
                                                    Get 5,000 steps every day for 21
                                                    days.
                                                </li>
                                            </ul>
                                        </div>
                                    </VaulDrawer>
                                    , we’ll give you a one year subscription for free.
                                </p>
                                */}
                                <TermsParagraph />
                            </div>
                            <div className="flex flex-col gap-2">
                                {[].length == 1 && iap == "iap" && (
                                    <typography.AsyncButton
                                        type="button"
                                        className={BUTTON_CLASS}
                                        onClick={() => {}}
                                    >
                                        restore
                                    </typography.AsyncButton>
                                )}
                                <typography.AsyncButton
                                    type="button"
                                    className={BUTTON_CLASS}
                                    forceLoading={isSubmitting}
                                    onClick={handleChallengeRegistration}
                                >
                                    Start the method
                                </typography.AsyncButton>
                            </div>
                        </form>
                    </div>
                </motion.div>
            ),
            actions: [],
        };
    }
    if (route == "about") {
        return {
            options: {
                title: "The Challenge",
                scene: {kind: "BLANK"},
            },
            view: (
                <form
                    style={{
                        backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
                    }}
                    className="
                        min-h-svh w-full scroll-smooth bg-cover bg-top font-inter
                    "
                >
                    <motion.div
                        className="
                            py-safe dark mx-auto flex h-dvh max-w-xl flex-col px-8
                            text-white
                        "
                        initial={{opacity: 0, y: 10}}
                        animate={{opacity: 1, y: 0}}
                        exit={{opacity: 0, y: 10}}
                    >
                        <Header back={() => router.push("base.index", {})} />

                        <div className="mt-8 flex flex-1 flex-col justify-between">
                            <h1 className="text-4xl font-extralight">The Method</h1>
                            <p className="text-2xl font-light">
                                Just 21 days. That’s it.
                            </p>
                            <ol
                                className="
                                    list-inside list-decimal space-y-4 text-lg
                                    font-extralight
                                "
                            >
                                <li>Weigh yourself every day.</li>
                                <li>
                                    <strong>Track everything you eat.</strong>{" "}
                                    <em>Everything.</em> Short on time? Take a picture
                                    instead.
                                </li>
                                <li>
                                    <strong>Workout 3 times a week</strong>. We’ll give
                                    you a simple — but effective — program.
                                </li>
                                <li>
                                    Get at least <strong>5,000 steps per day</strong>.
                                </li>
                            </ol>
                            <p className="text-2xl font-extralight">Can you do it?</p>

                            <div className="flex flex-col gap-2">
                                <button
                                    type="button"
                                    className={BUTTON_CLASS}
                                    onClick={() => setRoute("register")}
                                >
                                    Let’s do it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRoute("escape")}
                                    className="text-lg font-extralight text-white"
                                >
                                    I don’t want the method
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </form>
            ),
            actions: [],
        };
    }
    if (route == "workout") {
        return {
            options: {
                title: "Workout preferences",
                scene: {kind: "BLANK"},
            },
            view: (
                <Page
                    next={
                        options.mode == "registration"
                            ? () => setRoute("fine")
                            : {label: "Finish setup", onClick: handleFinishSetup}
                    }
                    back={() => setRoute("macros")}
                    disabled={settingsForm.values.workout == null}
                >
                    <div className="flex flex-1 flex-col justify-between pb-4">
                        <h1 className="text-3xl font-extralight">
                            Workout preferences
                        </h1>
                        <div
                            className="
                                flex flex-col gap-[2dvh] text-base font-extralight
                            "
                        >
                            <p>We’ll guide you every step of the way.</p>
                            <p>But we need to know what you’re comfortable with.</p>
                        </div>
                        <label className="space-y-2">
                            <onboarding.RadioSelect
                                field={settingsForm.fields.workout}
                                selected={settings.workout}
                                descriptions={{
                                    NONE: "I’ve never lifted weights before. I need a beginner program.",
                                    DUMBBELL:
                                        "I’ve lifted weights but want to stick to dumbbells.",
                                    BARBELL:
                                        "Give me a barbell program. I know what I’m doing.",
                                    SKIP: skipText,
                                }}
                            />
                        </label>
                    </div>
                </Page>
            ),
            actions: [],
        };
    }

    if (hasCustomMacros && route == "macros") {
        return {
            options: {
                title: "Macros",
                scene: {kind: "BLANK"},
            },
            view: (
                <Page
                    next={() => setRoute("workout")}
                    back={() => setRoute("activity")}
                    more={
                        <button
                            type="button"
                            className="
                                flex h-10 w-20 items-center justify-center rounded-sm
                                bg-[#332F4A] text-base font-light text-[#9874FF]
                            "
                            onClick={() => {
                                setHasCustomMacros(false);
                                void analytics.track("CLICKED_GUIDED_MACROS");
                            }}
                        >
                            guided
                        </button>
                    }
                >
                    {constants.CORE_METRICS.map(([metric, , , unit, label]) => (
                        <div key={metric}>
                            <label
                                htmlFor={metric}
                                className="text-lg font-extralight text-white"
                            >
                                {label}
                            </label>
                            <div className="flex">
                                <widgets.DecimalInput
                                    className="
                                        -mr-px block w-full grow rounded-l-md
                                        bg-[#332F4A] px-5 py-2.5 text-lg text-white
                                        outline-1 -outline-offset-1 outline-[#453F63]
                                        outline-solid
                                        placeholder:text-white/70
                                        focus-within:relative
                                        sm:text-sm/6
                                    "
                                    name={metric}
                                    value={
                                        targets[targetIndex]?.[`${metric}_target`] ??
                                        null
                                    }
                                    onChange={(value) => {
                                        setTargets((prev) => {
                                            const existingTarget =
                                                prev[targetIndex] == null
                                                    ? {
                                                          calories_target: null,
                                                          protein_target: null,
                                                          fat_target: null,
                                                          carbs_target: null,
                                                          fiber_target: null,
                                                          net_carbs_target: null,
                                                      }
                                                    : prev[targetIndex];

                                            return produce(prev, (draft) => {
                                                draft[targetIndex] = {
                                                    ...existingTarget,
                                                    [`${metric}_target`]: value,
                                                };
                                            });
                                        });
                                    }}
                                    readOnly={false}
                                />
                                <div
                                    className="
                                        flex shrink-0 items-center rounded-r-md
                                        bg-[#332F4A] px-3 text-base text-white/70
                                        outline-1 -outline-offset-1 outline-[#453F63]
                                        outline-solid
                                        sm:text-sm/6
                                    "
                                >
                                    {unit}
                                </div>
                            </div>
                        </div>
                    ))}
                </Page>
            ),
            actions: [],
        };
    }

    if (route == "macros") {
        return {
            options: {
                title: "Macros",
                scene: {kind: "BLANK"},
            },
            view: (
                <Page
                    next={() => setRoute("workout")}
                    back={() => setRoute("activity")}
                    more={
                        <button
                            type="button"
                            className="
                                flex h-10 w-16 items-center justify-center rounded-sm
                                bg-[#332F4A] text-base font-light text-[#9874FF]
                            "
                            onClick={() => {
                                setTargets((prev) => {
                                    return produce(prev, (draft) => {
                                        draft[targetIndex] = {
                                            ...prev[targetIndex],
                                            calories_target:
                                                handler.general.results.targets[0]
                                                    .calories_nearest,
                                            protein_target:
                                                handler.general.results.targets[0]
                                                    .protein_nearest,
                                            fat_target: null,
                                            carbs_target: null,
                                            fiber_target: null,
                                            net_carbs_target: null,
                                        };
                                    });
                                });
                                setHasCustomMacros(true);
                                void analytics.track("CLICKED_CUSTOM_MACROS");
                            }}
                        >
                            edit
                        </button>
                    }
                >
                    <div
                        className="
                            flex h-full flex-col justify-between py-10 text-center
                            font-extralight
                        "
                    >
                        <div className="text-4xl text-pretty">
                            Eat{" "}
                            <span className="text-[#7D50FF]">
                                {handler.general.results.targets[0].calories_nearest}
                            </span>{" "}
                            calories <br />
                            per day
                        </div>
                        <div className="contents text-2xl/8 text-pretty">
                            <div>
                                Get{" "}
                                <span className="text-[#7D50FF]">
                                    {handler.general.results.targets[0].protein_nearest}
                                </span>{" "}
                                grams of <span className="text-[#7D50FF]">protein</span>
                            </div>
                            <div>
                                Aim for{" "}
                                <span className="text-[#7D50FF]">
                                    {handler.general.results.targets[0].fat_nearest}
                                </span>{" "}
                                grams of <span className="text-[#7D50FF]">fat</span>
                            </div>
                            <div>
                                And{" "}
                                <span className="text-[#7D50FF]">
                                    {handler.general.results.targets[0].carbs_nearest}
                                </span>{" "}
                                grams of <span className="text-[#7D50FF]">carbs</span>
                            </div>
                            <div>
                                Eat <span className="text-[#7D50FF]">whole foods</span>{" "}
                                and <span className="text-[#7D50FF]">avoid</span>{" "}
                                processed foods.
                            </div>
                        </div>
                    </div>
                    <div className="mb-2 flex justify-center gap-2">
                        {/*
                            <button className="opacity-90 rounded-sm bg-white/10 px-4 py-1.5 text-xs font-medium text-white shadow-xs hover:bg-white/20">
                                settings
                            </button>
                            */}

                        <VaulDrawer
                            onOpen={() => {
                                void analytics.track("CLICKED_MACROS_LEARN_MORE");
                            }}
                            trigger={
                                <span
                                    className="
                                        rounded-sm bg-white/10 px-4 py-1.5 text-xs
                                        font-medium text-white opacity-90 shadow-xs
                                        hover:bg-white/20
                                    "
                                >
                                    learn more
                                </span>
                            }
                        >
                            <Drawer.Title className="sr-only">learn more</Drawer.Title>
                            <div
                                className="
                                    pb-safe flex flex-col gap-8 px-6 font-extralight
                                    text-white/85
                                "
                            >
                                <h3 className="text-2xl">About our recommendations</h3>
                                <div className="contents text-base">
                                    <p>
                                        We calculate your caloric needs based on your
                                        age, gender, height, weight, and activity level.
                                    </p>
                                    <p>
                                        Then we calculate a deficit or surplus based on
                                        your goal.
                                    </p>
                                    <p>
                                        This is the most important aspect of weight
                                        loss.
                                    </p>
                                    <p>
                                        If you eat below your caloric baseline, you will
                                        lose weight. Above, you’ll gain weight.
                                    </p>
                                    <p>
                                        You may notice a fairly high protein
                                        recommendation. Protein is the most satiating
                                        macronutrient. It helps you feel full longer. It
                                        promotes muscle gain and recovery.
                                    </p>
                                    <p>Eat as much protein as you want.</p>
                                </div>
                            </div>
                        </VaulDrawer>
                    </div>
                </Page>
            ),
            actions: [],
        };
    }

    if (route == "activity") {
        return {
            options: {
                title: "Activity level",
                scene: {kind: "BLANK"},
            },
            view: (
                <Page next={() => setRoute("macros")} back={() => setRoute("goal")}>
                    <h1 className="text-4xl font-extralight">How active are you?</h1>
                    <p className="text-lg font-extralight">
                        Do not factor in your workouts.
                        <br />
                        You should probably pick <strong>Sedentary</strong>.
                    </p>
                    <onboarding.RadioSelect
                        field={handler.settingsForm.fields.activity}
                        selected={handler.settingsForm.values.activity}
                        descriptions={{
                            SEDENTARY:
                                "You mostly sit around and don't move much throughout the day.",
                            ACTIVE: "You're on your feet most of the day. Your job involves a lot of walking.",
                            VERY_ACTIVE:
                                "You have an intensely physical job like construction. You're moving all day.",
                        }}
                    />
                </Page>
            ),
            actions: [],
        };
    }
    if (route == "goal") {
        return {
            options: {
                title: "What is your goal?",
                scene: {kind: "BLANK"},
            },
            view: (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    style={{
                        backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
                    }}
                    className="
                        dark min-h-svh w-full scroll-smooth bg-cover bg-top font-inter
                    "
                >
                    <div
                        className="
                            py-safe mx-auto flex h-dvh max-w-xl flex-col px-8 text-white
                        "
                    >
                        <Header back={() => setRoute("register")} />
                        <form className="mt-6 flex flex-1 flex-col justify-between">
                            <h1 className="text-4xl font-extralight">
                                What is your goal?
                            </h1>
                            <p className="text-xl font-extralight">
                                Helps us calculate your caloric needs.
                            </p>
                            <onboarding.RadioSelect
                                field={handler.settingsForm.fields.goal}
                                selected={handler.settingsForm.values.goal}
                                descriptions={{
                                    WEIGHT_LOSS:
                                        "Lose at most a pound a week. Mostly from fat. Sustainable long term.",
                                    AGGRESSIVE_WEIGHT_LOSS: false,
                                    HEALTH: "Get healthier and slowly replace fat with muscle.",
                                    BULK: "Put on muscle with minimal fat. Slow and steady.",
                                    MASS: false,
                                }}
                            />

                            <div className="flex flex-col gap-2">
                                <button
                                    type="button"
                                    className={BUTTON_CLASS}
                                    onClick={() => setRoute("activity")}
                                >
                                    Next
                                </button>
                            </div>
                        </form>
                    </div>
                </motion.div>
            ),
            actions: [],
        };
    }

    return {
        options: {
            title: "Your details",
            scene: {kind: "BLANK"},
        },
        view: (
            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                style={{
                    backgroundImage: `url('${BACKGROUND_IMAGES.workouts}')`,
                }}
                className="
                    dark min-h-svh w-full scroll-smooth bg-cover bg-top font-inter
                "
            >
                <div
                    className="
                        py-safe mx-auto flex h-dvh max-w-xl flex-col px-8 text-white
                    "
                >
                    <Header
                        back={
                            options.mode == "registration"
                                ? () => setRoute("about")
                                : options.mode == "welcome"
                                  ? () => router.push("today.journal", {})
                                  : () => router.push("today.summary", {})
                        }
                    />
                    <form
                        className={classNames(
                            "flex-1 flex flex-col justify-between",
                            window.cordova != null ? "mt-6" : "mt-2",
                        )}
                    >
                        {window.cordova != null && (
                            <>
                                <h1 className="text-4xl font-extralight">
                                    Your details
                                </h1>
                                <p className="text-xl font-extralight">
                                    Tell us a little about yourself.
                                </p>
                            </>
                        )}
                        <label className="flex flex-col gap-2 text-lg font-extralight">
                            <div>When were you born?</div>

                            <div
                                className="
                                    flex w-full items-center rounded-sm bg-[#332F4A]
                                    pr-1
                                    has-focus:ring-2 has-focus:ring-[#7D50FF]
                                "
                            >
                                <widgets.DateInput
                                    className="
                                        scheme-dark flex-1 border-0 bg-transparent px-6
                                        py-4
                                        placeholder:font-inter placeholder:font-thin
                                        placeholder:text-white
                                        focus:ring-0 focus:outline-hidden
                                    "
                                    name="date_of_birth"
                                    value={handler.profileForm.values.date_of_birth}
                                    onChange={(value) =>
                                        handler.profileForm.fields.date_of_birth.onChange(
                                            value ?? "",
                                        )
                                    }
                                    readOnly={false}
                                />
                            </div>
                        </label>
                        <label className="flex flex-col gap-2 text-lg font-extralight">
                            <div>
                                How tall are you in{" "}
                                <forms.PWidget
                                    field={settingsForm.fields.units}
                                    className={classNames(
                                        "form-select text-lg scheme-dark px-0 py-0 bg-none bg-transparent border-0 text-[#7D50FF] font-light focus:ring-[#7D50FF] focus:ring-1 rounded-sm",
                                        settingsForm.values.units == "IMPERIAL"
                                            ? "w-32"
                                            : "w-24",
                                    )}
                                />
                                ?
                            </div>
                            {settingsForm.values.units == "IMPERIAL" ? (
                                <div className="flex gap-4 [text-align-last:center]">
                                    <div
                                        className="
                                            flex w-full items-center rounded-sm
                                            bg-[#332F4A] pr-1
                                            has-focus:ring-2 has-focus:ring-[#7D50FF]
                                        "
                                    >
                                        <forms.PWidget
                                            field={settingsForm.fields.feet}
                                            className="
                                                form-select scheme-dark flex-1 border-0
                                                bg-transparent px-6 py-4
                                                placeholder:font-inter
                                                placeholder:font-thin
                                                placeholder:text-white
                                                focus:ring-0 focus:outline-hidden
                                            "
                                        />
                                    </div>
                                    <div
                                        className="
                                            flex w-full items-center rounded-sm
                                            bg-[#332F4A] pr-1
                                            has-focus:ring-2 has-focus:ring-[#7D50FF]
                                        "
                                    >
                                        <forms.PWidget
                                            className="
                                                form-select scheme-dark flex-1 border-0
                                                bg-transparent px-6 py-4
                                                placeholder:font-inter
                                                placeholder:font-thin
                                                placeholder:text-white
                                                focus:ring-0 focus:outline-hidden
                                            "
                                            field={settingsForm.fields.inches}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div
                                    className="
                                        flex w-full items-center rounded-sm bg-[#332F4A]
                                        pr-1
                                        has-focus:ring-2 has-focus:ring-[#7D50FF]
                                    "
                                >
                                    <widgets.LengthInput
                                        className="
                                            scheme-dark flex-1 border-0 bg-transparent
                                            px-6 py-4
                                            placeholder:font-inter placeholder:font-thin
                                            placeholder:text-white
                                            focus:ring-0 focus:outline-hidden
                                        "
                                        isImperial={false}
                                        name="height"
                                        ariaLabel="height"
                                        value={handler.profileForm.values.height}
                                        onChange={
                                            handler.profileForm.fields.height.onChange
                                        }
                                        readOnly={false}
                                    />
                                    <div className="relative -left-4">cm</div>
                                </div>
                            )}
                        </label>

                        <label className="flex flex-col gap-2 text-lg font-extralight">
                            <div>What’s your approximate weight?</div>
                            {settingsForm.values.units == "IMPERIAL" ? (
                                <div className="flex gap-4 [text-align-last:center]">
                                    <div
                                        className="
                                            flex w-full items-center rounded-sm
                                            bg-[#332F4A] pr-1
                                            has-focus:ring-2 has-focus:ring-[#7D50FF]
                                        "
                                    >
                                        <forms.PWidget
                                            field={settingsForm.fields.weightLb}
                                            className="
                                                form-select scheme-dark flex-1 border-0
                                                bg-transparent px-6 py-4
                                                placeholder:font-inter
                                                placeholder:font-thin
                                                placeholder:text-white
                                                focus:ring-0 focus:outline-hidden
                                            "
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex gap-4 [text-align-last:center]">
                                    <div
                                        className="
                                            flex w-full items-center rounded-sm
                                            bg-[#332F4A] pr-1
                                            has-focus:ring-2 has-focus:ring-[#7D50FF]
                                        "
                                    >
                                        <forms.PWidget
                                            field={settingsForm.fields.weightKg}
                                            className="
                                                form-select scheme-dark flex-1 border-0
                                                bg-transparent px-6 py-4
                                                placeholder:font-inter
                                                placeholder:font-thin
                                                placeholder:text-white
                                                focus:ring-0 focus:outline-hidden
                                            "
                                        />
                                    </div>
                                </div>
                            )}
                        </label>

                        <label className="flex flex-col gap-2 text-lg font-extralight">
                            <div>What’s your gender?</div>
                            <SimpleRadioSelect
                                field={settingsForm.fields.gender}
                                selected={settingsForm.values.gender}
                            />
                        </label>

                        <label className="flex flex-col gap-2 text-lg font-extralight">
                            <div>Have you used MyFitnessPal before?</div>
                            <SimpleRadioSelect
                                field={settingsForm.fields.mfp}
                                selected={settingsForm.values.mfp}
                            />
                        </label>

                        <div className="flex flex-col gap-2">
                            <button
                                type="button"
                                className={BUTTON_CLASS}
                                onClick={() => setRoute("goal")}
                                disabled={!isProfileComplete}
                            >
                                Next
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
        ),
        actions: [],
    };
}

export const Registration = router.Registration.useConditions((props) => {
    return useRegistration({
        mode: "registration",
        email: props.resolved.register.email,
        profile: null,
        activeProgram: null,
    });
});

export const Onboarding = router.Onboarding.useConditions((props) => {
    return useRegistration({
        mode: "onboarding",
        email: null,
        profile: props.profile,
        activeProgram: props.resolved.journal.program,
        lastWeight:
            props.resolved.journal.day.measurements.find((m) => m.weight != null)
                ?.weight ?? null,
    });
});

export const WelcomeOnboarding = router.WelcomeOnboarding.useConditions((props) => {
    return useRegistration({
        mode: "welcome",
        email: null,
        profile: props.profile,
        activeProgram: props.resolved.journal.program,
        lastWeight:
            props.resolved.journal.day.measurements.find((m) => m.weight != null)
                ?.weight ?? null,
    });
});
